import { Component, createRef } from "react"

/* Components */
import { DataLayout, Icon, Avatar } from "../../components"

/* Widgets */
import { FilterModal, INITIAL_FILTER, filterCount, periodName } from "../../widgets/dashboard"
import { DetailsModal } from "../../widgets/booking"

/* Shared */
import { CHANNELS, number, priceOf, statusOf } from "../../widgets/booking/shared"
import { organizationIcon } from "../../widgets/organization/shared"
import { initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { plural } from "../../widgets/category/shared"

/* Helpers */
import { utils, withRouter } from "../../helpers"

/* Constants */
import { env } from "../../constants"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* REST API */
import { get } from "../../api/Dashboard"

moment.locale("ru")


/* The colour every channel of a visit is drawn with — the same one in the ring and in the list */
const CHANNEL_COLORS = {
    owner: "#0279fc",
    app: "#7c4dff",
    web: "#00bfa5",
    "2gis": "#ff9800",
    qr: "#f4436c",
    share: "#00acc1",
    whatsapp: "#43a047"
}

/* A channel which the dictionary of the records does not know yet is still said out loud */
const channelName = channel => {

    if (!channel) {
        return "Приложение"
    }

    if (CHANNELS[channel]) {
        return CHANNELS[channel]
    }

    if (channel === "whatsapp") {
        return "WhatsApp"
    }

    return `${channel}`.charAt(0).toUpperCase() + `${channel}`.slice(1)
}

/* The words the state of a subscription is said with */
const SUBSCRIPTION_STATES = [
    { key: "active", text: "Активные", accent: "green" },
    { key: "expiring", text: "Истекают за 7 дней", accent: "orange" },
    { key: "pastDue", text: "Не оплачены", accent: "red" },
    { key: "pending", text: "Ожидают оплату", accent: "grey" },
    { key: "closed", text: "Закрыты", accent: "grey" }
]

/* The days of the week of the map of the hours, Monday first */
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]
const WEEKDAY_NAMES = ["понедельник", "вторник", "среда", "четверг", "пятница", "суббота", "воскресенье"]

/* The money in short: 1,2 млн ₸, 950 тыс ₸ */
const shortMoney = value => {

    const amount = Math.round(number(value))

    if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(1).replace(".", ",").replace(",0", "")} млн ₸`
    }

    if (amount >= 10000) {
        return `${Math.round(amount / 1000)} тыс ₸`
    }

    return utils.convertor(amount, "₸", 1)
}

/* A thin line of the values under the number of a box */
const Sparkline = ({ values }) => {

    if (!values || values.length < 2) {
        return null
    }

    const top = Math.max(...values, 1)
    const step = 100 / (values.length - 1)
    const points = values.map((value, index) => `${(index * step).toFixed(2)},${(30 - value / top * 26 - 2).toFixed(2)}`)

    return (
        <svg className="dashboard-spark" viewBox="0 0 100 30" preserveAspectRatio="none">
            <path className="area" d={`M0,30 L${points.join(" L")} L100,30 Z`} />
            <path className="line" d={`M${points.join(" L")}`} />
        </svg>
    )
}


/* Page Dashboard — the whole platform at one glance: what is going on today, what waits for a hand,
   and how the chosen period went against the same period before it */
class Dashboard extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: null,
            filter: { ...INITIAL_FILTER }
        }

        this._details = createRef()
        this._filter = createRef()
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load()
    }

    load = (reload = false) => {

        /* Fields */
        const { filter } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        get({
            start: moment(filter.startDate).format("YYYY-MM-DD"),
            end: moment(filter.endDate).format("YYYY-MM-DD")
        }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error", data: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null })
        })
    }

    /* How much it grew against the same period before. Growing out of nothing says nothing */
    growth = (value, before) => {

        const now = number(value)
        const past = number(before)

        if (past === 0) {
            return null
        }

        return Math.round((now - past) / past * 100)
    }

    /* The badge of the growth. "inverse" — for the numbers which are better when they fall */
    _growth = (value, before, inverse = false) => {

        const percent = this.growth(value, before)

        if (percent === null || percent === 0) {
            return null
        }

        let good = percent > 0

        if (inverse) {
            good = !good
        }

        return (
            <div className={`dashboard-growth ${good ? "good" : "bad"}`} title={`Прошлый такой же период: ${utils.convertor(Math.round(number(before)), "", 0)}`}>
                <Icon name={percent > 0 ? "up" : "down"} size={12} width={2.6} />
                {percent > 0 ? "+" : ""}{percent}%
            </div>
        )
    }

    _header = () => {

        /* Fields */
        const { filter } = this.state

        const active = filterCount(filter)

        return (
            <div className="dashboard-header">

                <div className="page-header-text">Dashboard</div>

                <div className="dashboard-header-tools">

                    <div onClick={() => this._filter.current.open(filter)} title="Период" className={`dashboard-header-period ${active > 0 ? "active" : ""}`}>
                        <div className="dashboard-header-period-icon">
                            <Icon name="calendar" size={18} />
                        </div>
                        <div className="dashboard-header-period-text">
                            {periodName(filter)}
                            <span>{moment(filter.startDate).format("DD.MM.YY")} — {moment(filter.endDate).format("DD.MM.YY")}</span>
                        </div>
                    </div>

                </div>

            </div>
        )
    }

    /* TODAY — the day which is going on now, whatever period is chosen */
    _today = () => {

        /* Fields */
        const { data } = this.state

        const today = data.today

        const items = [
            { key: "visits", icon: "booking", value: today.visits, text: "визитов сегодня", note: `${today.visitsDone} прошли · ${today.visitsAhead} впереди` },
            { key: "created", icon: "plus", value: today.created, text: "новых записей", note: today.createdYesterday > 0 ? `вчера к этому часу ${today.createdYesterday}` : "вчера к этому часу 0", trend: today.created - today.createdYesterday },
            { key: "queue", icon: "queue", value: today.queue, text: "в очереди сейчас", note: "ждут вызова" },
            { key: "users", icon: "user", value: today.users, text: "новых клиентов", note: "аккаунты за сегодня" },
            { key: "reviews", icon: "review", value: today.reviews, text: "отзывов", note: "написали сегодня" },
            { key: "money", icon: "wallet", value: shortMoney(today.money), text: "оплат подписки", note: "поступило сегодня" }
        ]

        return (
            <div className="dashboard-today">

                <div className="dashboard-today-list">
                    {items.map(item =>
                        <div key={item.key} className="dashboard-today-item">
                            <div className="dashboard-today-icon"><Icon name={item.icon} size={16} width={2} /></div>
                            <div className="dashboard-today-body">
                                <div className="dashboard-today-value">
                                    {typeof item.value === "number" ? item.value.toLocaleString("ru-RU") : item.value}
                                    {item.trend !== undefined && item.trend !== 0 && <em className={item.trend > 0 ? "up" : "down"}>{item.trend > 0 ? "▲" : "▼"}</em>}
                                </div>
                                <div className="dashboard-today-text">{item.text}</div>
                                <div className="dashboard-today-note">{item.note}</div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* ONE OF THE THREE MAIN BOXES — how many, for how much, what was really earned, and the line of the period */
    _box = settings => {

        const { icon, accent, title, note, count, total, revenue, before, chips, line, link } = settings
        const { navigate } = this.props

        return (
            <div className={`dashboard-box ${accent}`} onClick={() => navigate(link)}>

                <div className="dashboard-box-head">

                    <div className="dashboard-box-icon">
                        <Icon name={icon} size={20} />
                    </div>

                    <div className="dashboard-box-title">
                        {title}
                        <span>{note}</span>
                    </div>

                    {this._growth(count, before)}

                </div>

                <div className="dashboard-box-middle">
                    <div className="dashboard-box-count">{utils.convertor(Math.round(number(count)), "", 0)}</div>
                    <Sparkline values={line} />
                </div>

                <div className="dashboard-box-money">

                    <div className="dashboard-box-money-item">
                        <span>Сумма</span>
                        <b>{shortMoney(total)}</b>
                    </div>

                    <div className="dashboard-box-money-item">
                        <span>Оборот</span>
                        <b className="accent">{shortMoney(revenue)}</b>
                    </div>

                </div>

                <div className="dashboard-box-chips">
                    {chips.map(chip =>
                        <div className={`dashboard-box-chip ${chip.accent}`} key={chip.text}>
                            <i />
                            {chip.text}
                            <b>{utils.convertor(Math.round(number(chip.value)), "", 0)}</b>
                        </div>
                    )}
                </div>

            </div>
        )
    }

    _boxes = () => {

        /* Fields */
        const { data } = this.state

        const { bookings, queue, subscription } = data.boxes
        const list = data.series.list

        return (
            <div className="dashboard-boxes">

                {this._box({
                    icon: "booking",
                    accent: "navy",
                    title: "Записи",
                    note: "созданы за период",
                    link: "/booking",
                    count: bookings.count,
                    total: bookings.total,
                    revenue: bookings.revenue,
                    before: bookings.before.count,
                    line: list.map(item => item.count),
                    chips: [
                        { text: "Завершены", value: bookings.completed, accent: "blue" },
                        { text: "Приняты", value: bookings.confirmed, accent: "purple" },
                        { text: "Ожидание", value: bookings.waiting, accent: "orange" },
                        { text: "Отменены", value: bookings.cancelled, accent: "red" }
                    ]
                })}

                {this._box({
                    icon: "queue",
                    accent: "wine",
                    title: "Очередь",
                    note: number(queue.waitMinutes) > 0 ? `ждут в среднем ${Math.round(number(queue.waitMinutes))} мин` : "талоны за период",
                    link: "/queue",
                    count: queue.count,
                    total: queue.total,
                    revenue: queue.revenue,
                    before: queue.before.count,
                    line: list.map(item => item.queue),
                    chips: [
                        { text: "Обслужены", value: queue.done, accent: "blue" },
                        { text: "В очереди", value: queue.waiting, accent: "orange" },
                        { text: "Не пришли", value: queue.missed, accent: "red" },
                        { text: "Отменены", value: queue.cancelled, accent: "red" }
                    ]
                })}

                {this._box({
                    icon: "card",
                    accent: "dark",
                    title: "Подписка",
                    note: "платежи за период",
                    link: "/transaction",
                    count: subscription.count,
                    total: subscription.total,
                    revenue: subscription.revenue,
                    before: subscription.before.count,
                    line: list.map(item => item.payments),
                    chips: [
                        { text: "Успешные", value: subscription.success, accent: "blue" },
                        { text: "Неудачные", value: subscription.failed, accent: "red" },
                        { text: "Точек", value: subscription.organizations, accent: "purple" },
                        { text: "Продления", value: subscription.recurring, accent: "orange" }
                    ]
                })}

            </div>
        )
    }

    /* THE CHART — the visits of the period bucket by bucket, split by the states, with the
       period before drawn as a line under the bars */
    _chart = () => {

        /* Fields */
        const { data } = this.state

        const { unit, list } = data.series
        const top = Math.max(...list.map(item => Math.max(item.count, item.before)), 1)
        const all = list.reduce((sum, item) => sum + item.count, 0)
        const before = list.reduce((sum, item) => sum + item.before, 0)
        const revenue = list.reduce((sum, item) => sum + item.revenue, 0)

        /* The busiest bucket of the period */
        const peak = list.reduce((best, item) => (item.count > (best ? best.count : 0) ? item : best), null)

        const step = 100 / Math.max(list.length, 1)
        const line = list.map((item, index) => `${(index * step + step / 2).toFixed(2)},${(100 - item.before / top * 100).toFixed(2)}`)

        /* The labels under the bars — not more than eight, or they run into each other */
        const every = Math.max(1, Math.ceil(list.length / 8))
        const labelOf = day => unit === "month" ? moment(day).format("MMM YY") : moment(day).format("D MMM")

        return (
            <div className="dashboard-card dashboard-chart-card">

                <div className="dashboard-card-head">
                    <div>
                        <div className="dashboard-card-title">Динамика записей</div>
                        <div className="dashboard-chart-summary">
                            <b>{all.toLocaleString("ru-RU")}</b>
                            {this._growth(all, before)}
                            <span>{shortMoney(revenue)} оборот{peak && peak.count > 0 ? ` · пик ${labelOf(peak.day)} — ${peak.count}` : ""}</span>
                        </div>
                    </div>
                    <div className="dashboard-chart-legend">
                        <div><i className="completed" />Завершены</div>
                        <div><i className="active" />Активные</div>
                        <div><i className="cancelled" />Отменены</div>
                        <div><i className="before" />Прошлый период</div>
                    </div>
                </div>

                <div className="dashboard-chart">

                    <div className="dashboard-chart-grid">
                        <i /><i /><i /><i />
                    </div>

                    <div className="dashboard-chart-bars">
                        {list.map(item =>
                            <div key={item.day} className="dashboard-chart-column">
                                <div className="dashboard-chart-stack" style={{ height: `${item.count / top * 100}%` }}>
                                    <i className="cancelled" style={{ flex: item.cancelled }} />
                                    <i className="active" style={{ flex: item.active }} />
                                    <i className="completed" style={{ flex: item.completed }} />
                                </div>
                                <div className="dashboard-chart-tip">
                                    <b>{labelOf(item.day)}</b>
                                    <span>Всего <em>{item.count}</em></span>
                                    <span>Завершены <em>{item.completed}</em></span>
                                    <span>Активные <em>{item.active}</em></span>
                                    <span>Отменены <em>{item.cancelled}</em></span>
                                    <span>Оборот <em>{shortMoney(item.revenue)}</em></span>
                                    <span className="muted">Прошлый период <em>{item.before}</em></span>
                                </div>
                            </div>
                        )}
                    </div>

                    {before > 0 &&
                        <svg className="dashboard-chart-line" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <path d={`M${line.join(" L")}`} />
                        </svg>
                    }

                </div>

                <div className="dashboard-chart-axis">
                    {list.map((item, index) =>
                        <span key={item.day}>{index % every === 0 ? labelOf(item.day) : ""}</span>
                    )}
                </div>

            </div>
        )
    }

    /* WHAT WAITS FOR A HAND — every line is a door into the tab which solves it */
    _attention = () => {

        /* Fields */
        const { data } = this.state
        const { navigate } = this.props

        const attention = data.attention

        const items = [
            { key: "leads", icon: "lead", accent: "green", value: attention.leads, text: "лидов ждут звонка", hint: "звонок на сегодня, просрочен или заявку никто не взял", link: "/lead" },
            { key: "supports", icon: "support", accent: "red", value: attention.supports, text: "новых жалоб и обращений", hint: "никто ещё не взял в работу", link: "/support" },
            { key: "reviewComplaints", icon: "alert", accent: "orange", value: attention.reviewComplaints, text: "жалоб точек на отзывы", hint: "ждут решения модерации", link: "/review" },
            { key: "lowReviews", icon: "review", accent: "orange", value: attention.lowReviews, text: "плохих отзывов без ответа", hint: "1–2 звезды за 14 дней", link: "/review" },
            { key: "failedPayments", icon: "card", accent: "red", value: attention.failedPayments, text: "отказов оплаты", hint: "за 7 дней — проверьте карты точек", link: "/transaction" },
            { key: "otpFlood", icon: "otp", accent: "violet", value: attention.otpFlood, text: "номеров с флудом кодов", hint: "5+ кодов за сутки", link: "/otp" },
            { key: "servicesWithoutCategory", icon: "category", accent: "blue", value: attention.servicesWithoutCategory, text: "услуг без категории", hint: "в приложении падают в «Другие»", link: "/services" },
            { key: "silentOrganizations", icon: "organization", accent: "grey", value: attention.silentOrganizations, text: "точек замолчали", hint: "записи были, но не за 30 дней", link: "/organization" }
        ].filter(item => item.value > 0)

        return (
            <div className="dashboard-card dashboard-attention">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Требует внимания</div>
                    {items.length > 0 && <div className="dashboard-attention-count">{items.length}</div>}
                </div>

                {items.length === 0
                    ? <div className="dashboard-calm">
                        <div className="dashboard-calm-icon"><Icon name="check" size={26} width={2.2} /></div>
                        <b>Всё спокойно</b>
                        <span>Нет жалоб, отказов и забытых дел</span>
                    </div>
                    : <div className="dashboard-attention-list">
                        {items.map(item =>
                            <div key={item.key} onClick={() => navigate(item.link)} className={`dashboard-attention-item ${item.accent}`}>
                                <div className="dashboard-attention-icon"><Icon name={item.icon} size={16} width={2} /></div>
                                <div className="dashboard-attention-text">
                                    <b><em>{item.value.toLocaleString("ru-RU")}</em> {item.text}</b>
                                    <span>{item.hint}</span>
                                </div>
                                <Icon name="right" size={13} width={2.2} />
                            </div>
                        )}
                    </div>
                }

            </div>
        )
    }

    /* THE NUMBERS BESIDE — what else appeared on the platform over the very same period */
    _numbers = () => {

        /* Fields */
        const { data } = this.state

        const { secondary, boxes } = data
        const before = secondary.before

        const cards = [
            {
                icon: "user",
                accent: "blue",
                text: "Новые пользователи",
                value: number(secondary.users),
                before: before.users,
                note: `Всего ${utils.convertor(number(secondary.usersTotal), "", 0)}`
            },
            {
                icon: "organization",
                accent: "teal",
                text: "Новые организации",
                value: number(secondary.organizations),
                before: before.organizations,
                note: `Мастеров +${utils.convertor(number(secondary.members), "", 0)} · услуг +${utils.convertor(number(secondary.services), "", 0)}`
            },
            {
                icon: "close",
                accent: "red",
                text: "Отменённые записи",
                value: number(boxes.bookings.cancelled),
                before: before.cancelled,
                inverse: true,
                note: `Упущено ${shortMoney(boxes.bookings.cancelledTotal)}`
            },
            {
                icon: "review",
                accent: "orange",
                text: "Отзывы",
                value: number(secondary.reviews),
                before: before.reviews,
                note: secondary.rate ? `Средняя оценка ★ ${number(secondary.rate).toFixed(2).replace(".", ",")}` : "Пока без оценок"
            }
        ]

        return (
            <div className="dashboard-numbers">
                {cards.map(card =>
                    <div className="dashboard-number" key={card.text}>

                        <div className={`dashboard-number-icon ${card.accent}`}>
                            <Icon name={card.icon} size={17} width={2} />
                        </div>

                        <div className="dashboard-number-body">
                            <div className="dashboard-number-text">{card.text}</div>
                            <div className="dashboard-number-value">
                                {utils.convertor(card.value, "", 0)}
                                {this._growth(card.value, card.before, card.inverse)}
                            </div>
                            <div className="dashboard-number-note">{card.note}</div>
                        </div>

                    </div>
                )}
            </div>
        )
    }

    /* WHEN THE CLIENTS COME — the visits of the period by the day of the week and the hour */
    _heatmap = () => {

        /* Fields */
        const { data } = this.state

        const cells = data.heatmap

        if (cells.length === 0) {
            return (
                <div className="dashboard-card">
                    <div className="dashboard-card-head">
                        <div className="dashboard-card-title">Когда приходят клиенты</div>
                    </div>
                    <div className="dashboard-empty">За период записей не было</div>
                </div>
            )
        }

        /* The hours which matter — from the first to the last one with visits, never narrower than 9—21 */
        const hours = cells.map(cell => cell.hour)
        const first = Math.min(9, ...hours)
        const last = Math.max(21, ...hours)
        const range = []

        for (let hour = first; hour <= last; hour++) {
            range.push(hour)
        }

        const top = Math.max(...cells.map(cell => cell.count), 1)
        const peak = cells.reduce((best, cell) => (cell.count > best.count ? cell : best), cells[0])

        /* The busiest day of the week */
        const totals = WEEKDAYS.map((day, index) => cells.filter(cell => cell.weekday === index).reduce((sum, cell) => sum + cell.count, 0))
        const busiest = totals.indexOf(Math.max(...totals))

        const countOf = (weekday, hour) => {
            const cell = cells.find(item => item.weekday === weekday && item.hour === hour)
            return cell ? cell.count : 0
        }

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div>
                        <div className="dashboard-card-title">Когда приходят клиенты</div>
                        <div className="dashboard-card-subtitle">Пик — {WEEKDAY_NAMES[peak.weekday]}, {`${peak.hour}`.padStart(2, "0")}:00 · самый загруженный день — {WEEKDAY_NAMES[busiest]}</div>
                    </div>
                </div>

                <div className="dashboard-heatmap" style={{ "--hours": range.length }}>

                    <div className="dashboard-heatmap-row head">
                        <span />
                        {range.map(hour => <span key={hour}>{hour % 2 === 0 ? hour : ""}</span>)}
                    </div>

                    {WEEKDAYS.map((day, weekday) =>
                        <div key={day} className={`dashboard-heatmap-row ${weekday >= 5 ? "weekend" : ""}`}>
                            <span>{day}</span>
                            {range.map(hour => {

                                const count = countOf(weekday, hour)
                                const level = count / top

                                return (
                                    <i
                                        key={hour}
                                        title={`${day}, ${`${hour}`.padStart(2, "0")}:00 — ${count} ${plural(count, "визит", "визита", "визитов")}`}
                                        className={count > 0 ? "" : "empty"}
                                        style={{ "--level": count > 0 ? 0.12 + level * 0.88 : 0 }}
                                    />
                                )
                            })}
                        </div>
                    )}

                </div>

                <div className="dashboard-heatmap-scale">
                    <span>меньше</span>
                    {[0.15, 0.35, 0.55, 0.75, 1].map(level => <i key={level} style={{ "--level": level }} />)}
                    <span>больше</span>
                </div>

            </div>
        )
    }

    /* THE CHANNELS — a ring of the shares and the list beside it */
    _channels = () => {

        /* Fields */
        const { data } = this.state

        const list = data.channels
        const all = list.reduce((memory, item) => memory + number(item.count), 0)

        /* The ring is one conic gradient, a slice after a slice */
        let start = 0

        const slices = list.map(item => {
            const color = utils.check(CHANNEL_COLORS[item.channel], "#90a4ae")
            const size = all > 0 ? number(item.count) / all * 100 : 0
            const slice = `${color} ${start}% ${start + size}%`
            start += size
            return slice
        })

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Каналы записей</div>
                    <div className="dashboard-card-note">за период</div>
                </div>

                {list.length === 0
                    ? <div className="dashboard-empty">За период записей не было</div>
                    : <div className="dashboard-donut-box">

                        <div className="dashboard-donut" style={{ background: `conic-gradient(${slices.join(", ")})` }}>
                            <div className="dashboard-donut-hole">
                                <b>{utils.convertor(all, "", 0)}</b>
                                <span>{plural(all, "запись", "записи", "записей")}</span>
                            </div>
                        </div>

                        <div className="dashboard-donut-list">
                            {list.map(item => {

                                const color = utils.check(CHANNEL_COLORS[item.channel], "#90a4ae")
                                const percent = all > 0 ? Math.round(number(item.count) / all * 100) : 0

                                return (
                                    <div className="dashboard-donut-item" key={item.channel} title={`${utils.convertor(number(item.count), "", 0)} записей на ${shortMoney(item.total)}`}>
                                        <i style={{ background: color }} />
                                        <span>{channelName(item.channel)}</span>
                                        <em>{shortMoney(item.total)}</em>
                                        <b>{percent}%</b>
                                    </div>
                                )
                            })}
                        </div>

                    </div>
                }

            </div>
        )
    }

    /* THE LAST VISITS — the rows open the very same card the page of the records opens */
    _latest = () => {

        /* Fields */
        const { data } = this.state
        const { navigate } = this.props

        const list = data.latest

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Последние записи</div>
                    <div onClick={() => navigate("/booking")} className="dashboard-card-link">
                        Все записи
                        <Icon name="right" size={13} width={2.2} />
                    </div>
                </div>

                {list.length === 0
                    ? <div className="dashboard-empty">За период записей не было</div>
                    : <div className="dashboard-rows">
                        {list.map(item => {

                            const state = statusOf(item.status)
                            const name = item.user && `${item.user}`.trim() ? `${item.user}`.trim() : null

                            return (
                                <div onClick={() => this._details.current.open(item)} className="dashboard-row" key={`${item.id}`}>

                                    <Avatar className="dashboard-row-avatar" src={item.userImage ? `${env.mediapoint}users/${item.userImage}` : null} alt={name}>
                                        <span style={{ background: avatarColorOf(item.userID || item.id) }}>{name ? initialsOf(name) : <Icon name="user" size={17} width={2} />}</span>
                                    </Avatar>

                                    <div className="dashboard-row-body">
                                        <div className="dashboard-row-name">
                                            {name ? name : "Без имени"}
                                            <div className="dashboard-row-badge">№{utils.check(item.number, item.id)}</div>
                                        </div>
                                        <div className="dashboard-row-text">
                                            {utils.check(item.organization, "Организация удалена")} · <i className="dashboard-row-channel" style={{ background: utils.check(CHANNEL_COLORS[item.channel], "#90a4ae") }} />{channelName(item.channel)}
                                        </div>
                                    </div>

                                    <div className="dashboard-row-side">
                                        <div className="dashboard-row-price">{utils.convertor(priceOf(item), "₸", 1)}</div>
                                        <div className={`dashboard-row-state ${state.accent}`}>
                                            <i />
                                            {state.text}
                                        </div>
                                    </div>

                                    <div className="dashboard-row-time">
                                        <div className="dashboard-row-value">{moment(item.reservedTime).format("D MMM")}</div>
                                        <div className="dashboard-row-text">{moment(item.reservedTime).format("HH:mm")}</div>
                                    </div>

                                </div>
                            )
                        })}
                    </div>
                }

            </div>
        )
    }

    /* THE LAST COMPLAINTS — the clients and the masters together; a row leads to the tab */
    _complaints = () => {

        /* Fields */
        const { data } = this.state
        const { navigate } = this.props

        const list = data.complaints

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Последние жалобы</div>
                    <div onClick={() => navigate("/support")} className="dashboard-card-link">
                        Все жалобы
                        <Icon name="right" size={13} width={2.2} />
                    </div>
                </div>

                {list.length === 0
                    ? <div className="dashboard-empty">За период жалоб не было</div>
                    : <div className="dashboard-rows">
                        {list.map(item => {

                            const client = item.kind === "client"
                            const image = item.image ? `${env.mediapoint}${client ? "users" : "members"}/${item.image}` : null
                            const name = item.name && `${item.name}`.trim() ? `${item.name}`.trim() : null

                            return (
                                <div onClick={() => navigate("/support")} className="dashboard-row" key={`${item.kind}-${item.id}`}>

                                    <Avatar className="dashboard-row-avatar" src={image} alt={name}>
                                        <span style={{ background: avatarColorOf(item.id) }}>{name ? initialsOf(name) : <Icon name="user" size={17} width={2} />}</span>
                                    </Avatar>

                                    <div className="dashboard-row-body">
                                        <div className="dashboard-row-name">
                                            {name ? name : "Без имени"}
                                            <div className={`dashboard-row-badge ${client ? "" : "violet"}`}>{client ? "клиент" : "мастер"}</div>
                                        </div>
                                        <div className="dashboard-row-text">{utils.check(item.message, "Без текста")}</div>
                                    </div>

                                    <div className="dashboard-row-time">
                                        <div className="dashboard-row-value">{moment(item.createdAt).format("D MMM")}</div>
                                        <div className="dashboard-row-text">{moment(item.createdAt).format("HH:mm")}</div>
                                    </div>

                                </div>
                            )
                        })}
                    </div>
                }

            </div>
        )
    }

    /* THE POINTS WHICH WORKED THE MOST over the period — a row opens the card of the point */
    _top = () => {

        /* Fields */
        const { data } = this.state
        const { navigate } = this.props

        const list = data.top
        const largest = list.reduce((memory, item) => Math.max(memory, number(item.bookings)), 1)

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Топ организаций</div>
                    <div onClick={() => navigate("/organization")} className="dashboard-card-link">
                        Все точки
                        <Icon name="right" size={13} width={2.2} />
                    </div>
                </div>

                {list.length === 0
                    ? <div className="dashboard-empty">За период записей не было</div>
                    : <div className="dashboard-top">
                        {list.map((item, index) => {

                            const image = item.image ? `${env.mediapoint}organizations/${item.image}` : null

                            return (
                                <div onClick={() => navigate(`/organization?open=${item.id}`)} className="dashboard-top-item" key={`${item.id}`}>

                                    <div className="dashboard-top-place">{index + 1}</div>

                                    <Avatar className="dashboard-top-avatar" src={image} alt={item.name}>
                                        {organizationIcon()}
                                    </Avatar>

                                    <div className="dashboard-top-body">
                                        <div className="dashboard-top-name">{utils.check(item.name, "Без названия")}</div>
                                        <div className="dashboard-top-line"><i style={{ width: `${Math.max(6, number(item.bookings) / largest * 100)}%` }} /></div>
                                    </div>

                                    <div className="dashboard-top-numbers">
                                        <b>{utils.convertor(number(item.bookings), "", 0)}</b>
                                        <span>{shortMoney(item.revenue)}</span>
                                    </div>

                                </div>
                            )
                        })}
                    </div>
                }

            </div>
        )
    }

    /* THE SUBSCRIPTIONS TODAY — the money of a month and the points to call */
    _subscriptions = () => {

        /* Fields */
        const { data } = this.state
        const { navigate } = this.props

        const subscription = data.boxes.subscription
        const expiring = data.attention.expiring
        const total = SUBSCRIPTION_STATES.filter(state => state.key !== "expiring").reduce((sum, state) => sum + number(subscription[state.key]), 0)

        return (
            <div className="dashboard-card">

                <div className="dashboard-card-head">
                    <div className="dashboard-card-title">Подписки сейчас</div>
                    <div onClick={() => navigate("/transaction")} className="dashboard-card-link">
                        Транзакции
                        <Icon name="right" size={13} width={2.2} />
                    </div>
                </div>

                <div className="dashboard-monthly">
                    <div>
                        <span>Оборот в месяц</span>
                        <b>{utils.convertor(Math.round(number(subscription.monthly)), "₸", 1)}</b>
                    </div>
                    <div className="dashboard-monthly-year">
                        <span>в год</span>
                        <b>{shortMoney(number(subscription.monthly) * 12)}</b>
                    </div>
                </div>

                <div className="dashboard-states-bar">
                    {SUBSCRIPTION_STATES.filter(state => state.key !== "expiring").map(state =>
                        <i key={state.key} className={state.accent} style={{ flex: total > 0 ? number(subscription[state.key]) : 1 }} />
                    )}
                </div>

                <div className="dashboard-states">
                    {SUBSCRIPTION_STATES.map(state =>
                        <div className={`dashboard-state ${state.accent}`} key={state.key}>
                            <i />
                            <span>{state.text}</span>
                            <b>{utils.convertor(number(subscription[state.key]), "", 0)}</b>
                        </div>
                    )}
                </div>

                {expiring.length > 0 &&
                    <div className="dashboard-expiring">
                        <div className="dashboard-expiring-title">Кому позвонить</div>
                        {expiring.map(item => {

                            const days = moment(item.currentPeriodEnd).startOf("day").diff(moment().startOf("day"), "days")
                            const late = item.status === "past_due"

                            let when = days === 0 ? "сегодня" : `через ${days} ${plural(days, "день", "дня", "дней")}`

                            if (late) {
                                when = "не оплачена"
                            }

                            return (
                                <div key={`${item.id}`} onClick={() => navigate(`/organization?open=${item.id}`)} className="dashboard-expiring-item">
                                    <Avatar className="dashboard-top-avatar" src={item.image ? `${env.mediapoint}organizations/${item.image}` : null} alt={item.name}>
                                        {organizationIcon()}
                                    </Avatar>
                                    <b>{item.name}</b>
                                    <span className={late ? "late" : ""}>{when}{!late && parseInt(item.autoRenew) === 1 ? " · авто" : ""}</span>
                                </div>
                            )
                        })}
                    </div>
                }

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data } = this.state

        if (status !== "ok" || !data) {
            return (
                <div className="page-content">
                    <DataLayout status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._today()}

                {this._boxes()}

                <div className="dashboard-grid">
                    {this._chart()}
                    {this._attention()}
                </div>

                {this._numbers()}

                <div className="dashboard-grid">
                    {this._heatmap()}
                    {this._channels()}
                </div>

                <div className="dashboard-columns">

                    <div className="dashboard-column">
                        {this._latest()}
                        {this._complaints()}
                    </div>

                    <div className="dashboard-column">
                        {this._subscriptions()}
                        {this._top()}
                    </div>

                </div>

            </div>
        )
    }

    render = () => {
        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <DetailsModal ref={this._details} onChanged={() => this.load()} />

                <FilterModal ref={this._filter} onApply={filter => this.setState({ filter }, () => this.load(true))} />

            </div>
        )
    }
}

export default withRouter(Dashboard)
